import React from 'react';
import { TextField, TextFieldProps, Box, Typography } from '@mui/material';
import { zoullooColors } from '../../theme';

interface TextFieldWithCounterProps extends Omit<TextFieldProps, 'helperText'> {
  maxLength: number;
  value: string;
  showCounter?: boolean;
  helperText?: React.ReactNode;
}

/**
 * TextField with character counter that blocks input beyond maxLength
 * Shows warning color when approaching the limit
 */
const TextFieldWithCounter: React.FC<TextFieldWithCounterProps> = ({
  maxLength,
  value,
  showCounter = true,
  helperText,
  error,
  ...props
}) => {
  const currentLength = value?.length || 0;
  const remaining = maxLength - currentLength;
  const isNearLimit = remaining <= Math.ceil(maxLength * 0.1); // Warning at 10% remaining
  const isAtLimit = remaining <= 0;

  return (
    <Box>
      <TextField
        {...props}
        value={value}
        error={error}
        inputProps={{
          ...props.inputProps,
          maxLength,
        }}
      />
      <Box
        sx={{
          display: 'flex',
          justifyContent: helperText ? 'space-between' : 'flex-end',
          alignItems: 'center',
          mt: 0.5,
          px: 1.5,
        }}
      >
        {helperText && (
          <Typography
            variant="caption"
            color={error ? 'error' : 'text.secondary'}
          >
            {helperText}
          </Typography>
        )}
        {showCounter && (
          <Typography
            variant="caption"
            sx={{
              color: error
                ? 'error.main'
                : isAtLimit
                ? 'error.main'
                : isNearLimit
                ? 'warning.main'
                : zoullooColors.grey[450],
              fontWeight: isNearLimit || isAtLimit ? 500 : 400,
            }}
          >
            {currentLength}/{maxLength}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default TextFieldWithCounter;
